import React, { useState, useEffect, useRef } from 'react';
import { collection, onSnapshot, doc, addDoc, serverTimestamp, getDoc, setDoc, deleteDoc, getDocs, query } from '../firebase.config';
import { useLanguage } from '../contexts/LanguageContext';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany } from '../utils/dataAdapter';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { formatDateToCompanyFormat } from '../utils/dateUtils';
import { useTableSort, SortableHeader } from '../utils/tableSort';
import { useTablePagination } from '../utils/tablePagination';
import PaginationControls from '../utils/PaginationControls';
import { useNotification } from '../contexts/NotificationContext';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

// Fixed Asset Card Component
const FixedAssetCard = ({ asset, isExpanded, onToggle, onEdit, onDelete, companyCurrency }) => {
  const currentValue = asset.currentValue || asset.purchasePrice || 0;
  const depreciationAmount = (asset.purchasePrice || 0) - currentValue;
  const depreciationPercent = asset.purchasePrice ? ((depreciationAmount / asset.purchasePrice) * 100).toFixed(1) : 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
        onClick={() => onToggle(asset.id)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-800 truncate">
                {asset.assetName || 'Fixed Asset'}
              </h3>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {asset.category || 'Asset'}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {formatDateToCompanyFormat(asset.purchaseDate, {})} • {asset.depreciationRate || 0}% p.a.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(currentValue, companyCurrency)}
              </p>
              <p className="text-xs text-gray-500">Current Value</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(asset);
                }}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-blue-600 hover:bg-blue-100 rounded-full transition-colors touch-manipulation"
                title="Edit"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(asset.id);
                }}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-red-600 hover:bg-red-100 rounded-full transition-colors touch-manipulation"
                title="Delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              <button
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                <svg 
                  className={`w-4 h-4 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Asset Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Asset Name:</span>
                  <span className="font-medium">{asset.assetName || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Category:</span>
                  <span className="font-medium">{asset.category || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Purchase Date:</span>
                  <span className="font-medium">{formatDateToCompanyFormat(asset.purchaseDate, {})}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Depreciation Rate:</span>
                  <span className="font-medium">{asset.depreciationRate || 0}% per annum</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Financial Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Purchase Price:</span>
                  <span className="font-medium">{formatCurrency(asset.purchasePrice || 0, companyCurrency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Depreciation:</span>
                  <span className="font-medium text-red-600">{formatCurrency(depreciationAmount, companyCurrency)} ({depreciationPercent}%)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Current Value:</span>
                  <span className="font-bold text-green-600 text-lg">{formatCurrency(currentValue, companyCurrency)}</span>
                </div>
                {asset.description && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Description:</span>
                    <span className="font-medium">{asset.description}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function FixedAssets({ db, userId, isAuthReady, appId }) {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id;
  const { currency: companyCurrency } = useCompanyCurrency();
  const { t } = useLanguage();
  const { success, error, warning } = useNotification();

  const [assets, setAssets] = useState([]);
  const [expandedAssets, setExpandedAssets] = useState({});
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAssetId, setDeletingAssetId] = useState(null);

  // Form state
  const [assetName, setAssetName] = useState('');
  const [category, setCategory] = useState('IT Equipment');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [purchasePrice, setPurchasePrice] = useState('');
  const [depreciationRate, setDepreciationRate] = useState('20');
  const [description, setDescription] = useState('');

  // Filter states
  const [filterSearch, setFilterSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const categories = ['IT Equipment', 'Furniture', 'Machinery', 'Vehicle', 'Building', 'Land', 'Other'];

  // Calculate current value based on depreciation
  const calculateCurrentValue = (purchasePrice, purchaseDate, depreciationRate) => {
    if (!purchasePrice || !purchaseDate || !depreciationRate) return purchasePrice || 0;
    const purchase = new Date(purchaseDate);
    const now = new Date();
    const years = (now - purchase) / (1000 * 60 * 60 * 24 * 365);
    const depreciationAmount = (purchasePrice * depreciationRate / 100) * years;
    return Math.max(0, purchasePrice - depreciationAmount);
  };

  // Fetch assets
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const assetsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'fixedAssets');
      if (!assetsCollectionRef) {
        setAssets([]);
        return;
      }
      
      const unsubscribe = onSnapshot(assetsCollectionRef, (snapshot) => {
        const arr = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          const currentValue = calculateCurrentValue(
            data.purchasePrice,
            data.purchaseDate,
            data.depreciationRate
          );
          arr.push({ 
            id: doc.id, 
            ...data,
            currentValue 
          });
        });
        setAssets(arr);
      }, (err) => {
        console.error('Error fetching assets:', err);
        setAssets([]);
      });
      return () => {
        unsubscribe();
        setAssets([]);
      };
    } else {
      setAssets([]);
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  // Toggle expand
  const toggleExpand = (assetId) => {
    setExpandedAssets(prev => ({
      ...prev,
      [assetId]: !prev[assetId]
    }));
  };

  // Handle edit
  const handleEdit = (asset) => {
    setEditingAssetId(asset.id);
    setAssetName(asset.assetName || '');
    setCategory(asset.category || 'IT Equipment');
    setPurchaseDate(asset.purchaseDate || new Date().toISOString().split('T')[0]);
    setPurchasePrice(asset.purchasePrice?.toString() || '');
    setDepreciationRate(asset.depreciationRate?.toString() || '20');
    setDescription(asset.description || '');
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = (assetId) => {
    setDeletingAssetId(assetId);
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!db || !userId || !appId || !deletingAssetId) return;
    try {
      const assetRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, 'fixedAssets'), deletingAssetId);
      await deleteDoc(assetRef);
      success('Fixed asset deleted successfully');
      setShowDeleteConfirm(false);
      setDeletingAssetId(null);
    } catch (err) {
      error('Error deleting asset: ' + err.message);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!db || !userId || !appId) return;
    if (!assetName.trim()) {
      warning('Please enter asset name');
      return;
    }
    if (!purchasePrice || parseFloat(purchasePrice) <= 0) {
      warning('Please enter valid purchase price');
      return;
    }

    try {
      const assetsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'fixedAssets');
      const currentValue = calculateCurrentValue(
        parseFloat(purchasePrice),
        purchaseDate,
        parseFloat(depreciationRate)
      );
      const assetData = {
        assetName: assetName.trim(),
        category,
        purchaseDate,
        purchasePrice: parseFloat(purchasePrice),
        depreciationRate: parseFloat(depreciationRate),
        currentValue,
        description: description.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (editingAssetId) {
        const assetRef = doc(assetsCollectionRef, editingAssetId);
        await setDoc(assetRef, assetData, { merge: true });
        success('Fixed asset updated successfully');
      } else {
        await addDoc(assetsCollectionRef, assetData);
        success('Fixed asset added successfully');
      }

      // Reset form
      setAssetName('');
      setCategory('IT Equipment');
      setPurchaseDate(new Date().toISOString().split('T')[0]);
      setPurchasePrice('');
      setDepreciationRate('20');
      setDescription('');
      setEditingAssetId(null);
      setShowForm(false);
    } catch (err) {
      error('Error saving asset: ' + err.message);
    }
  };

  // Filter assets
  const filteredAssets = (assets || []).filter(asset => {
    const matchesSearch = !filterSearch || 
      (asset.assetName || '').toLowerCase().includes(filterSearch.toLowerCase()) ||
      (asset.category || '').toLowerCase().includes(filterSearch.toLowerCase());
    const matchesCategory = !filterCategory || asset.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Table sorting
  const { sortConfig, handleSort, getSortedData } = useTableSort([], { key: 'purchaseDate', direction: 'desc' });
  const sortedAssets = getSortedData(filteredAssets);
  const pagination = useTablePagination(sortedAssets || [], 10);

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Fixed Assets</h1>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingAssetId(null);
            setAssetName('');
            setCategory('IT Equipment');
            setPurchaseDate(new Date().toISOString().split('T')[0]);
            setPurchasePrice('');
            setDepreciationRate('20');
            setDescription('');
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md"
        >
          + Add Fixed Asset
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Search assets..."
            />
          </div>
          <div className="sm:w-48">
            <AppSelect
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              aria-label="Filter by category"
              sheetTitle="Category"
              options={[
                { value: '', label: 'All Categories' },
                ...categories.map((cat) => ({ value: cat, label: cat })),
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} text-sm`}
            />
          </div>
        </div>
      </div>

      {/* Assets List */}
      <div className="space-y-4">
        {(pagination.currentData || []).map(asset => (
          <FixedAssetCard
            key={asset.id}
            asset={asset}
            isExpanded={expandedAssets[asset.id]}
            onToggle={toggleExpand}
            onEdit={handleEdit}
            onDelete={handleDelete}
            companyCurrency={companyCurrency}
          />
        ))}
        {(!pagination.currentData || pagination.currentData.length === 0) && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">No fixed assets found</p>
            <p className="text-sm mt-2">Add your first fixed asset to get started</p>
          </div>
        )}
      </div>

      <PaginationControls {...pagination} />

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[92dvh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <h2 className="text-2xl font-bold mb-4">
                {editingAssetId ? 'Edit Fixed Asset' : 'Add Fixed Asset'}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Name *
                  </label>
                  <input
                    type="text"
                    value={assetName}
                    onChange={(e) => setAssetName(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="e.g., Laptop, Office Desk"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <AppSelect
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      aria-label="Category"
                      sheetTitle="Category"
                      options={categories.map((cat) => ({ value: cat, label: cat }))}
                      triggerClassName={APP_SELECT_FILTER_TRIGGER}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Purchase Date *
                    </label>
                    <input
                      type="date"
                      value={purchaseDate}
                      onChange={(e) => setPurchaseDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Purchase Price *
                    </label>
                    <input
                      type="number"
                      value={purchasePrice}
                      onChange={(e) => setPurchasePrice(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Depreciation Rate (% p.a.)
                    </label>
                    <input
                      type="number"
                      value={depreciationRate}
                      onChange={(e) => setDepreciationRate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="20"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                    placeholder="Additional details about the asset"
                  />
                </div>
                {purchasePrice && purchaseDate && depreciationRate && (
                  <div className="bg-blue-50 p-4 rounded-md">
                    <p className="text-sm text-gray-700">
                      <strong>Current Value:</strong> {formatCurrency(
                        calculateCurrentValue(
                          parseFloat(purchasePrice),
                          purchaseDate,
                          parseFloat(depreciationRate)
                        ),
                        companyCurrency
                      )}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingAssetId(null);
                  }}
                  className="flex-1 min-h-[44px] bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 min-h-[44px] bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg touch-manipulation"
                >
                  {editingAssetId ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6">
            <h3 className="text-lg font-bold mb-4">Confirm Delete</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this fixed asset? This action cannot be undone.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingAssetId(null);
                }}
                className="flex-1 min-h-[44px] bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg touch-manipulation"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 min-h-[44px] bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg touch-manipulation"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FixedAssets;

